import { createFileRoute } from "@tanstack/react-router";
import { breadcrumbSchema } from "@/lib/schema";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import cover from "@/assets/market/bp02-kv.webp.asset.json";

const URL = "https://palstars.com/booster-pack/legends-awaken";
const TITLE = "Legends Awaken Booster Pack Pull Rates (EBP02)";
const DESC =
  "Palworld Legends Awaken (EBP02) booster pack pull rates, 7-card pack layout, hit-slot odds and pre-release FAQ ahead of the October 30, 2026 launch.";

const ODDS = [
  { r: "R", p: 60.8 },
  { r: "RR", p: 20 },
  { r: "SR", p: 10 },
  { r: "SP", p: 5 },
  { r: "OSR", p: 2.5 },
  { r: "SSP", p: 1.2 },
  { r: "SSS", p: 0.5 },
];

const FAQ: FaqItem[] = [
  {
    q: "When does the Legends Awaken expansion release?",
    a: "Booster Pack Legends Awaken (EBP02) has a global street date of October 30, 2026 for the English Palworld Official Card Game.",
  },
  {
    q: "How many cards are in the EBP02 set list?",
    a: "Bushiroad has not published the full English EBP02 card list yet. Pal Stars will add every base card number and parallel treatment the day the official list goes live, and won't guess at counts before then.",
  },
  {
    q: "How can I avoid card scalper pricing at launch?",
    a: "Compare launch listings against how Dawn of Palpagos sealed product behaved after its restocks, check the retailer's announced MSRP before paying a premium, and wait out the first week when asking prices are usually at their most inflated.",
  },
];

export const Route = createFileRoute("/booster-pack_/legends-awaken")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:image", content: `https://palstars.com${cover.url}` },
      { property: "og:image:alt", content: "Official Palworld Legends Awaken EBP02 key visual" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://palstars.com${cover.url}` },
      { name: "robots", content: "index, follow, max-snippet:-1" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Booster Packs", "/booster-pack"], ["Legends Awaken", "/booster-pack/legends-awaken"]]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: LegendsAwakenPack,
});

function LegendsAwakenPack() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-8">
      <header className="glass rounded-2xl p-6">
        <p className="text-xs uppercase tracking-widest text-primary">Set code EBP02 · Release October 30, 2026</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld Legends Awaken Booster Pack Pull Rates</h1>
        <p className="mt-3 inline-block rounded-md border border-primary/50 px-2 py-1 font-mono text-xs text-primary">
          [SEALED ALLOCATIONS TRACKING ACTIVE]
        </p>
        <p className="aeo-answer mt-4 text-sm text-muted-foreground leading-relaxed">
          Legends Awaken is the second English booster set for the Palworld card game. If you want to see where current
          sets are trading before it lands, the{" "}
          <a href="/tcg-price-index" className="text-primary hover:underline">Palworld TCG price index market trends</a>{" "}
          page tracks every printed card daily.
        </p>
      </header>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Legends Awaken Booster Box Pull Rates and Rarities</h2>
        <p className="aeo-answer mt-3 text-sm text-muted-foreground leading-relaxed">
          Each pack holds 7 cards. Slots one through four are Commons, slots five and six are Uncommons, and the seventh
          slot is the hit. That hit is always R or better. Based on the Dawn of Palpagos structure, it resolves to R about
          60.8% of the time, RR 20%, SR 10%, SP 5%, OSR 2.5%, SSP 1.2% and SSS 0.5%. Put another way, you'd expect roughly
          one SSS for every 200 packs, or a little over one in every 16 twelve-pack boxes.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          These are the published Dawn of Palpagos ratios. We'll update this table if Bushiroad announces different odds
          for EBP02.
        </p>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {ODDS.map((o) => (
            <div key={o.r} className="rounded-xl border border-border/60 bg-card/40 p-3 text-center">
              <div className="text-lg font-bold">{o.r}</div>
              <div className="text-sm tabular-nums text-primary">{o.p}%</div>
              <div className="mt-1 text-[11px] text-muted-foreground">≈1 in {Math.round(100 / o.p)} packs</div>
            </div>
          ))}
        </div>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
          Planning a group box break or a bulk preorder? The{" "}
          <a href="/marketplace" className="text-primary hover:underline">Palworld TCG bulk orders platform</a> lists
          sealed product from the major marketplaces side by side.
        </p>
      </section>

      <FaqSection items={FAQ} heading="Legends Awaken Pre-Release FAQ" />
    </div>
  );
}
